import { IonButton, IonButtons, IonCardContent, IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonRow, IonSpinner, IonText } from '@ionic/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { refresh } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';

import { ILabourClocking } from '../../../models/dms/clocking.model';
import { IRepairOrder } from '../../../models/dms/repairOrder.model';
import { IStockUnitDetails } from '../../../models/dms/stockUnit.model';
import store from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchStockUnitDetailsStoreAsync, setSelectedStockUnitStoreAsync } from '../../../store/stockUnitSlice';
import { setLabourClockingRepairOrderStoreAsync } from '../../../store/technicianClockingSlice';
import { IonListWrapper } from './StockUnitAssetDetails.styles';
import { AttachmentAssetType, IAttachmentContainer } from '../../../models/dms/attachment.model';
import { setSelectedVehicleAttachmentsStoreAsync } from '../../../store/vehicleAttachmentSlice';
import SelectedVehicleAttachments from '../../selectedVehicleAttachments/SelectedVehicleAttachments';

interface IComponentProps {
  repairOrder: IRepairOrder;
}

const StockUnitAssetDetails: React.FC<IComponentProps> = ({ repairOrder }) => {
  const [loadingError, setLoadingError] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const { selectedStockUnit, selectedStockUnitLoading } = useAppSelector((state) => state.stockUnit);
  const { selectedVehicleAttachments } = store.getState().vehicleAttachments;
  const { networkConnected } = useAppSelector((state) => state.app);

  const dispatch = useAppDispatch();

  useEffect(() => {
    loadRepairOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRepairOrder = async () => {
    const { selectedStockUnit } = store.getState().stockUnit;

    // There is a chance that the stock number, vehicle (and other details) will be null. This happens when the RO is clocked on
    // automatically from the server or from clocking history and no info is available in the cache to fill this information.

    const sourceRef = repairOrder.vehicle.stockNumber || parseInt(repairOrder.vehicle.sourceRef) || 0;
    const sourceRefStr = sourceRef.toString();

    await dispatch(
      setSelectedVehicleAttachmentsStoreAsync({
        assetType: AttachmentAssetType.StockUnit,
        key: sourceRefStr,
        jobCode: '',
        jobType: '',
        repairOrderNumber: repairOrder.repairOrderNumber?.toString() || '-1',
        mediaRef: '',
        attachments: selectedVehicleAttachments?.key === sourceRefStr ? [...selectedVehicleAttachments.attachments] : [],
      } as IAttachmentContainer),
    );

    if (sourceRef) {
      // Stock number is available
      if (sourceRef !== selectedStockUnit?.id) {
        // Reset vehicle details to default
        dispatch(
          setSelectedStockUnitStoreAsync({
            id: sourceRef,
          } as IStockUnitDetails),
        );
        // Load workshop vehicle
        await loadStockUnit(sourceRef || -1, repairOrder.repairOrderNumber || -1);
      }
    } else {
      // Stock number (and other values) are unavailable. Load the repair order to get the stock number
      try {
        if (repairOrder.repairOrderNumber) {
          setLoadingData(true);
          const resultAction = await dispatch(setLabourClockingRepairOrderStoreAsync(repairOrder.repairOrderNumber));
          const result = unwrapResult(resultAction) as ILabourClocking;
          loadStockUnit(result.repairOrder.vehicle.stockNumber || -1, repairOrder.repairOrderNumber);
        }
      } catch (ex) {
        setLoadingError(true);
      } finally {
        setLoadingData(false);
      }
    }
  };

  const loadStockUnit = async (stockNumber: number, repairOrderNumber: number) => {
    try {
      setLoadingData(true);
      const resultAction = await dispatch(fetchStockUnitDetailsStoreAsync({ stockNumber, repairOrderNumber }));
      unwrapResult(resultAction);
      setLoadingError(false);
    } catch (ex) {
      setLoadingError(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleClickLoadRepairOrderDetails = async () => {
    await loadStockUnit(repairOrder.vehicle.stockNumber || parseInt(repairOrder.vehicle.sourceRef) || -1, repairOrder.repairOrderNumber || -1);
  };

  return (
    <IonListWrapper>
      {/* Header */}
      <IonItem>
        <IonButtons slot="end">
          <IonButton onClick={() => handleClickLoadRepairOrderDetails()} fill="clear" disabled={selectedStockUnitLoading || loadingData}>
            <IonIcon icon={refresh} slot="icon-only" color="primary" />
          </IonButton>
        </IonButtons>
        Vehicle Details
      </IonItem>

      {/* Data */}
      <IonItem lines="none">
        <IonCardContent>
          <IonGrid>
            <IonRow>
              <IonCol size="auto">
                <IonItem>
                  <IonLabel position="stacked">Stock Number</IonLabel>
                  <IonText class="flexed flexed-center">{selectedStockUnit?.id || ''}</IonText>
                  {(selectedStockUnitLoading || loadingData) && <IonSpinner name="dots" className="text-spinner" />}
                </IonItem>
              </IonCol>
              <IonCol size="auto">
                <IonItem>
                  <IonLabel position="stacked">Make</IonLabel>
                  <IonText class="flexed flexed-center">{selectedStockUnit?.make || ''}</IonText>
                  {(selectedStockUnitLoading || loadingData) && <IonSpinner name="dots" className="text-spinner" />}
                </IonItem>
              </IonCol>
              <IonCol size="auto">
                <IonItem>
                  <IonLabel position="stacked">Model</IonLabel>
                  <IonText class="flexed flexed-center">{selectedStockUnit?.model || ''}</IonText>
                  {(selectedStockUnitLoading || loadingData) && <IonSpinner name="dots" className="text-spinner" />}
                </IonItem>
              </IonCol>
              <IonCol size="auto">
                <IonItem>
                  <IonLabel position="stacked">VIN</IonLabel>
                  <IonText class="flexed flexed-center">{selectedStockUnit?.vin || ''}</IonText>
                  {(selectedStockUnitLoading || loadingData) && <IonSpinner name="dots" className="text-spinner" />}
                </IonItem>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonCardContent>
      </IonItem>
      {selectedStockUnit?.id && repairOrder.repairOrderNumber && <SelectedVehicleAttachments />}
      {loadingError && (
        <IonItem lines="none" disabled={selectedStockUnitLoading || loadingData}>
          <IonText color="danger">{networkConnected && <h4>Failed to load stock unit details</h4>}</IonText>
        </IonItem>
      )}
    </IonListWrapper>
  );
};

export default StockUnitAssetDetails;
